import React, { useState, useMemo } from "react";
import { Box, Typography, LinearProgress, Alert, useTheme, useMediaQuery } from "@mui/material";
import { useMsal } from "@azure/msal-react";
import "../styles.css";

import CustomSnackbar from "../components/CustomSnackbar";
import ApprovedOrdersHistory from "../components/ApproveLines/ApprovedOrdersHistory";
import OrderCard from "../components/ApproveLines/OrderCard";
import OrderCardMobile from "../components/ApproveLines/OrderCardMobile";
import HeaderTabs from "../components/HeaderTabs";

import { useUser } from "../contexts/UserContext";
import useApproveLines from "./ApproveLines/hooks/useApproveLines";
import useOrderFiltering from "./ApproveLines/hooks/useOrderFiltering";
import OrderFilters from "./ApproveLines/components/OrderFilters";
import OrderFiltersMobile from "./ApproveLines/components/OrderFiltersMobile";
import { handleExportOrdersToExcel } from "./ApproveLines/utils/orderExport";

const ApproveLinesPage = () => {
  const { instance } = useMsal();
  const { user } = useUser();
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();
  // Matches the sx breakpoint the desktop/mobile views used to be toggled
  // with via CSS display — now used to mount only the active one, since
  // rendering both (just hidden) doubled every card list's render cost.
  const isDesktopView = useMediaQuery(theme.breakpoints.up("md"));

  const {
    ordersList,
    loading,
    loadingMore,
    approvingOrderId,
    serialNumbers,
    snackbar,
    setSnackbar,
    handleQuantityChange,
    handleLineCheckboxChange,
    handleDeltaCheckboxChange,
    handleConfirmCheckedLines,
    handleToggleReviewed,
    fetchSerialNumbers,
    updateOrderData,
  } = useApproveLines(tabValue);

  const {
    searchTerm,
    setSearchTerm,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    reviewedFilter,
    setReviewedFilter,
    filteredOrders,
  } = useOrderFiltering(ordersList);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Rebuilt only when ordersList itself changes (not on every filter
  // keystroke) so looking up an order's index doesn't rescan the whole list
  // once per rendered card — that's what made clearing the filter (which can
  // jump filteredOrders back up to the full, large ordersList) slow.
  const orderIndexById = useMemo(
    () => new Map(ordersList.map((o, i) => [o._uniqueId, i])),
    [ordersList]
  );

  const getOrderIndex = (order) => {
    const index = orderIndexById.get(order._uniqueId);
    return index === undefined ? -1 : index;
  };

  return (
    <div className="App">
      <Box
        flexDirection="column"
        gap={4}
        p={2}
        sx={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}
      >
        <HeaderTabs
          tabValue={tabValue}
          handleTabChange={handleTabChange}
          labels={["אישור הזמנות ", " הסטוריית אישורים"]}
        />
        
        {tabValue === 0 ? (
          <Box display="flex" flexDirection="column" gap={4} p={2}>
            {isDesktopView ? (
            <Box>
              {!loading && (
                <OrderFilters
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  onExport={() => handleExportOrdersToExcel(filteredOrders)}
                  hasOrders={filteredOrders && filteredOrders.length > 0}
                  reviewedFilter={reviewedFilter}
                  setReviewedFilter={setReviewedFilter}
                />
              )}

              {loading && (
                <Box>
                  <LinearProgress sx={{ marginTop: 2 }} />
                  <Typography variant="body1" mt={2}>
                    אנא המתן...
                  </Typography>
                </Box>
              )}

              {!loading && loadingMore && (
                <Alert
                  severity="info"
                  sx={{ mb: 2, border: "2px solid #1976d2", fontWeight: 600 }}
                >
                  טוען הזמנות נוספות — החיפוש והייצוא לאקסל עשויים להיות חלקיים עד סיום הטעינה
                </Alert>
              )}

              {!loading && filteredOrders.length === 0 && (
                <Typography mt={2}>לא נמצאו הזמנות</Typography>
              )}

              {!loading &&
                filteredOrders.length > 0 &&
                filteredOrders.map((order) => {
                  const orderIndex = getOrderIndex(order);
                  return (
                    <OrderCard
                      key={order._uniqueId}
                      order={order}
                      handleConfirmcheckedLines={handleConfirmCheckedLines}
                      orderIndex={orderIndex}
                      handleQuantityChange={handleQuantityChange}
                      handleDeltaCheckboxChange={handleDeltaCheckboxChange}
                      handleLineCheckboxChange={handleLineCheckboxChange}
                      fetchSerialNumbers={fetchSerialNumbers}
                      serialNumbers={serialNumbers}
                      updateOrderData={updateOrderData}
                      onToggleReviewed={handleToggleReviewed}
                      isApproving={approvingOrderId === order._uniqueId}
                      searchTerm={searchTerm}
                    />
                  );
                })}

              <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                title={snackbar.title}
                onClose={handleCloseSnackbar}
              />
            </Box>
            ) : (
            <Box>
              {!loading && (
                <OrderFiltersMobile
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  onExport={() => handleExportOrdersToExcel(filteredOrders)}
                  reviewedFilter={reviewedFilter}
                  setReviewedFilter={setReviewedFilter}
                />
              )}

              {loading && <LinearProgress />}

              {!loading && loadingMore && (
                <Alert
                  severity="info"
                  sx={{ mb: 2, border: "2px solid #1976d2", fontWeight: 600 }}
                >
                  טוען הזמנות נוספות — החיפוש והייצוא לאקסל עשויים להיות חלקיים עד סיום הטעינה
                </Alert>
              )}

              {!loading && filteredOrders.length === 0 && (
                <Typography mt={2}>לא נמצאו הזמנות</Typography>
              )}

              {!loading &&
                filteredOrders.length > 0 &&
                filteredOrders.map((order) => {
                  const orderIndex = getOrderIndex(order);
                  return (
                    <OrderCardMobile
                      key={order._uniqueId}
                      order={order}
                      orderIndex={orderIndex}
                      handleQuantityChange={handleQuantityChange}
                      handleLineCheckboxChange={handleLineCheckboxChange}
                      handleDeltaCheckboxChange={handleDeltaCheckboxChange}
                      serialNumbers={serialNumbers}
                      handleConfirmcheckedLines={handleConfirmCheckedLines}
                      updateOrderData={updateOrderData}
                      fetchSerialNumbers={fetchSerialNumbers}
                      onToggleReviewed={handleToggleReviewed}
                      isApproving={approvingOrderId === order._uniqueId}
                      searchTerm={searchTerm}
                    />
                  );
                })}

              <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                title={snackbar.title}
                onClose={handleCloseSnackbar}
              />
            </Box>
            )}
          </Box>
        ) : (
          <ApprovedOrdersHistory email={user.email} />
        )}
      </Box>
    </div>
  );
};

export default ApproveLinesPage;